export interface AffectedComponent {
  id?: string
  name: string
  group_name: string
  current_status?: string
}

export interface Incident {
  id: string
  name: string
  status?: string
  url: string
  last_update_message?: string
  last_update_at?: string
  current_worst_impact?: string
  affected_components?: AffectedComponent[]
}

export interface FetchStatusIncidentsOptions {
  signal?: AbortSignal
}

const STATUS_URL = 'https://status-proxy.buffer.com'

type UnknownRecord = Record<string, unknown>

const isRecord = (value: unknown): value is UnknownRecord =>
  typeof value === 'object' && value !== null

const toAffectedComponent = (value: unknown): AffectedComponent | undefined => {
  if (
    !isRecord(value) ||
    typeof value.name !== 'string' ||
    typeof value.group_name !== 'string'
  ) {
    return undefined
  }

  return {
    ...(typeof value.id === 'string' ? { id: value.id } : {}),
    name: value.name,
    group_name: value.group_name,
    ...(typeof value.current_status === 'string'
      ? { current_status: value.current_status }
      : {}),
  }
}

const toAffectedComponents = (
  value: unknown,
): AffectedComponent[] | undefined => {
  if (!Array.isArray(value)) {
    return undefined
  }

  const components = value.map(toAffectedComponent)
  return components.every((component) => component !== undefined)
    ? components
    : undefined
}

const toIncident = (value: unknown): Incident | undefined => {
  if (
    !isRecord(value) ||
    typeof value.id !== 'string' ||
    typeof value.name !== 'string'
  ) {
    return undefined
  }

  return {
    id: value.id,
    name: value.name,
    ...(typeof value.status === 'string' ? { status: value.status } : {}),
    url: typeof value.url === 'string' ? value.url : '',
    ...(typeof value.last_update_message === 'string'
      ? { last_update_message: value.last_update_message }
      : {}),
    ...(typeof value.last_update_at === 'string'
      ? { last_update_at: value.last_update_at }
      : {}),
    ...(typeof value.current_worst_impact === 'string'
      ? { current_worst_impact: value.current_worst_impact }
      : {}),
    affected_components: toAffectedComponents(value.affected_components),
  }
}

const toIncidents = (value: unknown): Incident[] => {
  if (!isRecord(value) || !Array.isArray(value.ongoing_incidents)) {
    return []
  }

  return value.ongoing_incidents.flatMap((incident) => {
    const normalizedIncident = toIncident(incident)
    return normalizedIncident === undefined ? [] : [normalizedIncident]
  })
}

export async function fetchStatusIncidents({
  signal,
}: FetchStatusIncidentsOptions = {}): Promise<Incident[]> {
  try {
    const res = await fetch(STATUS_URL, { signal })
    if (!res.ok) {
      throw new Error(`Status proxy returned ${res.status}`)
    }
    return toIncidents(await res.json())
  } catch (e: unknown) {
    if (e instanceof Error && e.name !== 'AbortError') {
      // eslint-disable-next-line
      console.error(e.message)
    }
    throw e
  }
}
